import { useCallback, useEffect, useState } from "react";
import { History, Pencil, Trash2, Plus, X, PackageMinus, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, gbp } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useLockBodyScroll from "../hooks/useLockBodyScroll";

const inputCls = "h-10 px-2 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";

export default function IngredientsPage() {
  const { user } = useAuth();
  const isPro = user?.tier === "pro";
  const [ingredients, setIngredients] = useState(null);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", canonical_unit: "kg", yield_factor: 1, latest_unit_price: 0, category: "food", expected_uses: null });
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [wasting, setWasting] = useState(null);
  const [wasteForm, setWasteForm] = useState({ quantity: "", unit: "kg", reason: "" });
  const [addingProduct, setAddingProduct] = useState(false);
  const [productForm, setProductForm] = useState({ name: "", latest_unit_price: "" });
  const [mapping, setMapping] = useState(null);
  const [mapForm, setMapForm] = useState({ target_ingredient_id: "", product_name: "" });

  useLockBodyScroll(Boolean(editing || adding || history || wasting || mapping));

  const load = useCallback(() => api.get("/ingredients").then((r) => setIngredients(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/ingredients/${editing.id}`, {
        name: editing.name,
        yield_factor: parseFloat(editing.yield_factor),
        latest_unit_price: parseFloat(editing.latest_unit_price),
        category: editing.category || "food",
        expected_uses: editing.category === "sundry" && editing.expected_uses ? parseInt(editing.expected_uses) : null,
      });
      if (isPro) {
        await api.patch(`/ingredients/${editing.id}/stock`, { stock_on_hand: parseFloat(editing.stock_on_hand) || 0 });
        await api.patch(`/ingredients/${editing.id}/threshold`, {
          reorder_threshold: editing.reorder_threshold === "" || editing.reorder_threshold == null
            ? null : parseFloat(editing.reorder_threshold),
        });
      }
      toast.success("Ingredient updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const logWaste = async () => {
    try {
      await api.post(`/ingredients/${wasting.id}/waste`, {
        quantity: parseFloat(wasteForm.quantity) || 0,
        unit: wasteForm.unit,
        reason: wasteForm.reason,
      });
      toast.success("Waste logged");
      setWasting(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const create = async () => {
    try {
      await api.post("/ingredients", {
        ...addForm,
        yield_factor: parseFloat(addForm.yield_factor),
        latest_unit_price: parseFloat(addForm.latest_unit_price),
      });
      toast.success("Ingredient added");
      setAdding(false);
      setAddForm({ name: "", canonical_unit: "kg", yield_factor: 1, latest_unit_price: 0, category: "food", expected_uses: null });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/ingredients/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openHistory = (ingredient) => {
    setHistory(ingredient);
    setAddingProduct(false);
    setProductForm({ name: "", latest_unit_price: "" });
  };

  const createProduct = async () => {
    try {
      const { data } = await api.post(`/ingredients/${history.id}/products`, {
        name: productForm.name,
        latest_unit_price: productForm.latest_unit_price === "" ? null : parseFloat(productForm.latest_unit_price),
      });
      toast.success("Product added");
      setHistory(data);
      setAddingProduct(false);
      setProductForm({ name: "", latest_unit_price: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const setDefaultProduct = async (productId) => {
    try {
      const { data } = await api.post(`/ingredients/${history.id}/products/${productId}/set-default`);
      toast.success("Default product updated");
      setHistory(data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeProduct = async (productId) => {
    try {
      const { data } = await api.delete(`/ingredients/${history.id}/products/${productId}`);
      toast.success("Product deleted");
      setHistory(data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openMapping = (source) => {
    const target = ingredients.find((ingredient) => ingredient.id !== source.id);
    setMapping(source);
    setMapForm({ target_ingredient_id: target?.id || "", product_name: source.name });
  };

  const mergeIntoProduct = async () => {
    const target = ingredients.find((ingredient) => ingredient.id === mapForm.target_ingredient_id);
    try {
      await api.post(`/ingredients/${mapping.id}/merge-into-product`, mapForm);
      toast.success(`${mapping.name} is now a product of ${target.name}`);
      setMapping(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!ingredients) return <p className="text-ink/50 py-10">Loading…</p>;

  return (
    <div className="space-y-6 animate-rise" data-testid="ingredients-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Ingredient store</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">Ingredients</h1>
        </div>
        <button data-testid="add-ingredient-button" onClick={() => setAdding(true)}
          className="h-11 px-4 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium flex items-center gap-2 transition-colors duration-200">
          <Plus size={16} /> Add
        </button>
      </div>

      {isPro && ingredients.length > 0 && (
        <p className="text-sm text-ink/50" data-testid="ingredients-stock-value">
          Stock on hand: <span className="font-mono-num font-semibold text-ink">
            {gbp(ingredients.reduce((sum, ing) => sum + (ing.stock_on_hand || 0) * ((ing.latest_unit_price || 0) / (ing.yield_factor || 1)), 0))}
          </span>
        </p>
      )}

      <div className="flex gap-2" role="tablist" aria-label="Ingredient category">
        {[['all', 'All'], ['food', 'Food'], ['sundry', 'Sundries']].map(([value, label]) => (
          <button key={value} onClick={() => setCategoryFilter(value)} className={`px-3 py-1.5 rounded-full text-sm ${categoryFilter === value ? "bg-ink text-white" : "bg-surface text-ink/60"}`}>{label}</button>
        ))}
      </div>

      {ingredients.filter((ing) => categoryFilter === "all" || (ing.category || "food") === categoryFilter).length === 0 ? (
        <div className="bg-surface border border-dashed border-sand rounded-lg p-10 text-center" data-testid="ingredients-empty-state">
          <p className="text-ink/60 text-sm">Nothing here yet. Snap a receipt and your store fills itself.</p>
        </div>
      ) : (
        <div className="divide-y divide-sand" data-testid="ingredients-list">
          <div className="hidden sm:grid grid-cols-12 gap-2 py-2 text-xs uppercase tracking-[0.2em] text-ink/40 font-medium">
            <span className="col-span-4">Name</span><span className="col-span-2 text-right">Price</span>
            <span className="col-span-2 text-right">Yield</span><span className="col-span-2 text-right">True cost</span>
            <span className="col-span-2" />
          </div>
          {ingredients.filter((ing) => categoryFilter === "all" || (ing.category || "food") === categoryFilter).map((ing) => (
            <div key={ing.id} data-testid={`ingredient-row-${ing.id}`} className="grid grid-cols-12 gap-2 py-3 items-center">
              <div className="col-span-12 sm:col-span-4">
                <p className="font-medium text-sm">{ing.name}
                  <span className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${ing.category === "sundry" ? "bg-almond/20 text-almond" : "bg-surface text-ink/50"}`}>{ing.category === "sundry" ? "sundry" : "food"}</span>
                  {ing.is_new && <span className="ml-2 text-[10px] uppercase tracking-wider bg-almond/20 text-almond px-1.5 py-0.5 rounded">new: check yield</span>}
                </p>
                <p className="text-xs text-ink/40">per {ing.canonical_unit}</p>
              </div>
              <p className="col-span-4 sm:col-span-2 text-right font-mono-num text-sm">{gbp(ing.latest_unit_price)}</p>
              <p className="col-span-3 sm:col-span-2 text-right font-mono-num text-sm text-ink/60">{Math.round((ing.yield_factor || 1) * 100)}%</p>
              <p className="col-span-5 sm:col-span-2 text-right font-mono-num text-sm font-semibold">
                {gbp((ing.latest_unit_price || 0) / (ing.yield_factor || 1) / (ing.category === "sundry" ? (ing.expected_uses || 1) : 1))}
              </p>
              <div className="col-span-12 sm:col-span-2 flex justify-end gap-1">
                <button data-testid={`ingredient-history-${ing.id}`} onClick={() => openHistory(ing)} className="p-2 rounded hover:bg-sand text-ink/50" title="Price history"><History size={15} /></button>
                {isPro && (
                  <button data-testid={`ingredient-waste-${ing.id}`}
                    onClick={() => { setWasting(ing); setWasteForm({ quantity: "", unit: ing.canonical_unit, reason: "" }); }}
                    className="p-2 rounded hover:bg-sand text-ink/50" title="Log waste"><PackageMinus size={15} /></button>
                )}
                <button data-testid={`ingredient-map-${ing.id}`} onClick={() => openMapping(ing)} disabled={ingredients.length < 2}
                  className="p-2 rounded hover:bg-sand text-ink/50 disabled:opacity-30" title="Map as product of..."><GitMerge size={15} /></button>
                <button data-testid={`ingredient-edit-${ing.id}`} onClick={() => setEditing({ ...ing })} className="p-2 rounded hover:bg-sand text-ink/50" title="Edit"><Pencil size={15} /></button>
                <button data-testid={`ingredient-delete-${ing.id}`} onClick={() => remove(ing.id)} className="p-2 rounded hover:bg-sand text-ink/50" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(editing || adding) && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => { setEditing(null); setAdding(false); }}>
          <div className="bg-cream rounded-lg p-6 w-full max-w-md space-y-4 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="ingredient-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-xl">{adding ? "Add ingredient" : "Edit ingredient"}</h3>
              <button onClick={() => { setEditing(null); setAdding(false); }} className="p-1 text-ink/40"><X size={18} /></button>
            </div>
            {adding ? (
              <>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Name</label>
                  <input data-testid="ingredient-name-input" className={`${inputCls} mt-1`} value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  {['food', 'sundry'].map((category) => <button key={category} type="button" onClick={() => setAddForm({ ...addForm, category, expected_uses: category === "food" ? null : addForm.expected_uses })} className={`flex-1 h-10 rounded-md border text-sm ${addForm.category === category ? "bg-ink text-white" : "border-sand"}`}>{category === "food" ? "Food" : "Sundry"}</button>)}
                </div>
                {addForm.category === "sundry" && <>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!addForm.expected_uses} onChange={(e) => setAddForm({ ...addForm, expected_uses: e.target.checked ? 1 : null })} /> Is this reusable across multiple events?</label>
                  {!!addForm.expected_uses && <div><label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">How many uses before you'd replace it?</label><input type="number" min="1" step="1" className={`${inputCls} mt-1`} value={addForm.expected_uses} onChange={(e) => setAddForm({ ...addForm, expected_uses: e.target.value })} /></div>}
                </>}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Unit</label>
                    <select data-testid="ingredient-unit-select" className={`${inputCls} mt-1`} value={addForm.canonical_unit} onChange={(e) => setAddForm({ ...addForm, canonical_unit: e.target.value })}>
                      <option>kg</option><option>L</option><option>each</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Yield (0–1)</label>
                    <input type="number" step="0.05" min="0.05" max="1" data-testid="ingredient-yield-input" className={`${inputCls} mt-1 font-mono-num`} value={addForm.yield_factor} onChange={(e) => setAddForm({ ...addForm, yield_factor: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">£/unit</label>
                    <input type="number" step="0.01" data-testid="ingredient-price-input" className={`${inputCls} mt-1 font-mono-num`} value={addForm.latest_unit_price} onChange={(e) => setAddForm({ ...addForm, latest_unit_price: e.target.value })} />
                  </div>
                </div>
                <button data-testid="ingredient-save-button" onClick={create} className="w-full h-11 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-colors duration-200">Add ingredient</button>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Name</label>
                  <input data-testid="ingredient-edit-name-input" className={`${inputCls} mt-1`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  {['food', 'sundry'].map((category) => <button key={category} type="button" onClick={() => setEditing({ ...editing, category, expected_uses: category === "food" ? null : (editing.expected_uses || 1) })} className={`flex-1 h-10 rounded-md border text-sm ${(editing.category || "food") === category ? "bg-ink text-white" : "border-sand"}`}>{category === "food" ? "Food" : "Sundry"}</button>)}
                </div>
                {editing.category === "sundry" && <>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.expected_uses} onChange={(e) => setEditing({ ...editing, expected_uses: e.target.checked ? 1 : null })} /> Is this reusable across multiple events?</label>
                  {!!editing.expected_uses && <div><label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">How many uses before you'd replace it?</label><input type="number" min="1" step="1" className={`${inputCls} mt-1`} value={editing.expected_uses} onChange={(e) => setEditing({ ...editing, expected_uses: e.target.value })} /></div>}
                </>}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Yield factor (0–1)</label>
                    <input type="number" step="0.05" min="0.05" max="1" data-testid="ingredient-edit-yield-input" className={`${inputCls} mt-1 font-mono-num`} value={editing.yield_factor} onChange={(e) => setEditing({ ...editing, yield_factor: e.target.value })} />
                    <p className="text-[11px] text-ink/40 mt-1">e.g. 0.85 = 15% lost to trim/cook-down</p>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">£ per {editing.canonical_unit}</label>
                    <input type="number" step="0.01" data-testid="ingredient-edit-price-input" className={`${inputCls} mt-1 font-mono-num`} value={editing.latest_unit_price} onChange={(e) => setEditing({ ...editing, latest_unit_price: e.target.value })} />
                  </div>
                </div>
                {isPro && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Stock on hand</label>
                      <input type="number" step="any" min="0" data-testid="ingredient-edit-stock-input" className={`${inputCls} mt-1 font-mono-num`}
                        value={editing.stock_on_hand ?? 0} onChange={(e) => setEditing({ ...editing, stock_on_hand: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Reorder at</label>
                      <input type="number" step="any" min="0" placeholder="none" data-testid="ingredient-edit-threshold-input" className={`${inputCls} mt-1 font-mono-num`}
                        value={editing.reorder_threshold ?? ""} onChange={(e) => setEditing({ ...editing, reorder_threshold: e.target.value })} />
                    </div>
                  </div>
                )}
                <button data-testid="ingredient-update-button" onClick={save} className="w-full h-11 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-colors duration-200">Save changes</button>
              </>
            )}
          </div>
        </div>
      )}

      {history && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setHistory(null)}>
          <div className="bg-cream rounded-lg p-6 w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto space-y-4 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="price-history-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-xl">{history.name} price history</h3>
              <button onClick={() => setHistory(null)} className="p-1 text-ink/40"><X size={18} /></button>
            </div>
            <section className="space-y-3 border-b border-sand pb-4" data-testid="ingredient-products-section">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Products</h4>
                {!addingProduct && (
                  <button type="button" onClick={() => setAddingProduct(true)} className="text-sm text-tea-text font-medium hover:underline">
                    + Add product
                  </button>
                )}
              </div>
              {(history.products || []).length === 0 ? (
                <p className="text-sm text-ink/50">No products mapped yet.</p>
              ) : (
                <div className="divide-y divide-sand">
                  {history.products.map((product) => (
                    <div key={product.id} className="py-2 flex items-center justify-between gap-3">
                      <p className="min-w-0 text-sm">
                        <span className="font-medium">{product.name}</span>
                        {product.is_default && <span className="ml-2 text-[10px] uppercase tracking-wider bg-tea/20 text-tea-text px-1.5 py-0.5 rounded">default</span>}
                        <span className="text-ink/50"> &middot; {gbp(product.latest_unit_price)}/{history.canonical_unit}</span>
                      </p>
                      <div className="flex items-center justify-end gap-1 shrink-0">
                        {!product.is_default && (
                          <button type="button" onClick={() => setDefaultProduct(product.id)}
                            className="min-h-10 px-2 text-xs text-tea-text font-medium hover:underline">
                            Set as default
                          </button>
                        )}
                        <button type="button" onClick={() => removeProduct(product.id)} title={`Delete ${product.name}`}
                          className="w-10 h-10 rounded hover:bg-sand text-ink/40 flex items-center justify-center">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {addingProduct && (
                <div className="space-y-3 rounded-md bg-surface p-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Product name</label>
                      <input autoFocus className={`${inputCls} mt-1`} value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">£/{history.canonical_unit}</label>
                      <input type="number" min="0" step="0.01" className={`${inputCls} mt-1 font-mono-num`} value={productForm.latest_unit_price}
                        onChange={(e) => setProductForm({ ...productForm, latest_unit_price: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={createProduct} className="h-10 px-4 rounded-md bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium transition-colors duration-200">Add product</button>
                    <button type="button" onClick={() => setAddingProduct(false)} className="h-10 px-3 rounded-md text-sm text-ink/50 hover:bg-sand">Cancel</button>
                  </div>
                </div>
              )}
            </section>
            <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Ingredient-level history</h4>
            {(history.price_history || []).length === 0 ? (
              <p className="text-sm text-ink/50">No purchases logged yet.</p>
            ) : (
              <div className="divide-y divide-sand max-h-72 overflow-y-auto">
                {[...history.price_history].reverse().map((h, i) => (
                  <div key={`${h.date}-${h.price}-${h.supplier}-${i}`} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-ink/60">{h.date} · {h.supplier}</span>
                    <span className="font-mono-num font-medium">{gbp(h.price)}/{history.canonical_unit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {wasting && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setWasting(null)}>
          <div className="bg-cream rounded-lg p-6 w-full max-w-md space-y-4 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="waste-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-xl">Log waste — {wasting.name}</h3>
              <button onClick={() => setWasting(null)} className="p-1 text-ink/40"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Quantity</label>
                <input type="number" step="any" min="0" autoFocus data-testid="waste-quantity-input" className={`${inputCls} mt-1 font-mono-num`}
                  value={wasteForm.quantity} onChange={(e) => setWasteForm({ ...wasteForm, quantity: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Unit</label>
                <select data-testid="waste-unit-select" className={`${inputCls} mt-1`} value={wasteForm.unit}
                  onChange={(e) => setWasteForm({ ...wasteForm, unit: e.target.value })}>
                  <option>kg</option><option>g</option><option>L</option><option>ml</option><option>each</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Reason (optional)</label>
              <input data-testid="waste-reason-input" className={`${inputCls} mt-1`} placeholder="e.g. spoiled, over-prepped"
                value={wasteForm.reason} onChange={(e) => setWasteForm({ ...wasteForm, reason: e.target.value })} />
            </div>
            <button data-testid="waste-save-button" onClick={logWaste} className="w-full h-11 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-colors duration-200">Log waste</button>
          </div>
        </div>
      )}

      {mapping && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setMapping(null)}>
          <div className="bg-cream rounded-lg p-6 w-full max-w-md space-y-4 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="map-product-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-xl">Map as product of...</h3>
              <button onClick={() => setMapping(null)} className="p-1 text-ink/40"><X size={18} /></button>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Target ingredient</label>
              <select data-testid="map-product-target" className={`${inputCls} mt-1`} value={mapForm.target_ingredient_id}
                onChange={(e) => setMapForm({ ...mapForm, target_ingredient_id: e.target.value })}>
                {ingredients.filter((ingredient) => ingredient.id !== mapping.id).map((ingredient) => (
                  <option key={ingredient.id} value={ingredient.id}>{ingredient.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Product name</label>
              <input data-testid="map-product-name" className={`${inputCls} mt-1`} value={mapForm.product_name}
                onChange={(e) => setMapForm({ ...mapForm, product_name: e.target.value })} />
            </div>
            <p className="text-sm text-warnamber bg-warnamber/10 rounded-md p-3">
              Recipes using {mapping.name} directly will be reassigned to the target ingredient.
            </p>
            <button data-testid="map-product-confirm" onClick={mergeIntoProduct} disabled={!mapForm.target_ingredient_id}
              className="w-full h-11 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-colors duration-200 disabled:opacity-50">
              Map product
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
